// DOM-based heads-up display: crosshair, hotbar, hearts, mining progress, FPS
// and the death overlay. Kept out of the Three.js scene for crisp UI text.

import { HOTBAR_SIZE, Inventory } from '../sim/inventory';
import { itemName } from '../sim/items';
import { MAX_HEALTH } from '../sim/player';
import { itemSwatch } from './swatch';

export class Hud {
  private root: HTMLDivElement;
  private hotbarEl: HTMLDivElement;
  private heartsEl: HTMLDivElement;
  private fpsEl: HTMLDivElement;
  private statusEl: HTMLDivElement;
  private progressEl: HTMLDivElement;
  private deathEl: HTMLDivElement;
  private hintEl: HTMLDivElement;
  private underwaterEl: HTMLDivElement;
  private debugEl: HTMLDivElement;
  private onRespawn: (() => void) | null = null;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.style.cssText =
      'position:fixed;inset:0;pointer-events:none;font-family:system-ui,sans-serif;color:#fff;user-select:none;';
    parent.appendChild(this.root);

    // Underwater tint overlay (so submersion is visible).
    this.underwaterEl = document.createElement('div');
    this.underwaterEl.style.cssText =
      'position:absolute;inset:0;background:rgba(30,90,170,.42);display:none;';
    this.root.appendChild(this.underwaterEl);

    // Debug overlay (F3), hidden by default.
    this.debugEl = document.createElement('div');
    this.debugEl.style.cssText =
      'position:absolute;left:10px;top:42px;font:12px/1.5 monospace;white-space:pre;' +
      'background:rgba(0,0,0,.5);padding:6px 8px;border-radius:4px;display:none;';
    this.root.appendChild(this.debugEl);

    // Crosshair.
    const cross = document.createElement('div');
    cross.style.cssText =
      'position:absolute;left:50%;top:50%;width:20px;height:20px;transform:translate(-50%,-50%);';
    cross.innerHTML =
      '<div style="position:absolute;left:9px;top:2px;width:2px;height:16px;background:rgba(255,255,255,.8)"></div>' +
      '<div style="position:absolute;left:2px;top:9px;width:16px;height:2px;background:rgba(255,255,255,.8)"></div>';
    this.root.appendChild(cross);

    // Mining progress bar under the crosshair.
    this.progressEl = document.createElement('div');
    this.progressEl.style.cssText =
      'position:absolute;left:50%;top:calc(50% + 20px);width:60px;height:6px;transform:translateX(-50%);' +
      'background:rgba(0,0,0,.4);border:1px solid rgba(255,255,255,.5);display:none;';
    this.progressEl.innerHTML = '<div style="height:100%;width:0;background:#fff"></div>';
    this.root.appendChild(this.progressEl);

    // Hearts.
    this.heartsEl = document.createElement('div');
    this.heartsEl.style.cssText =
      'position:absolute;left:50%;bottom:78px;transform:translateX(-50%);display:flex;gap:2px;';
    this.root.appendChild(this.heartsEl);

    // Hotbar.
    this.hotbarEl = document.createElement('div');
    this.hotbarEl.style.cssText =
      'position:absolute;left:50%;bottom:20px;transform:translateX(-50%);display:flex;gap:4px;';
    this.root.appendChild(this.hotbarEl);

    // FPS + status (top-left).
    this.fpsEl = document.createElement('div');
    this.fpsEl.style.cssText =
      'position:absolute;left:10px;top:10px;font-size:13px;background:rgba(0,0,0,.4);padding:4px 8px;border-radius:4px;';
    this.root.appendChild(this.fpsEl);

    this.statusEl = document.createElement('div');
    this.statusEl.style.cssText =
      'position:absolute;right:10px;top:10px;font-size:13px;background:rgba(0,0,0,.4);padding:4px 8px;border-radius:4px;text-align:right;';
    this.root.appendChild(this.statusEl);

    // Controls hint (bottom-left).
    this.hintEl = document.createElement('div');
    this.hintEl.style.cssText =
      'position:absolute;left:10px;bottom:10px;font-size:12px;line-height:1.5;background:rgba(0,0,0,.35);padding:6px 10px;border-radius:4px;max-width:230px;';
    this.hintEl.innerHTML =
      '<b>Click</b> to lock mouse<br><b>WASD</b> move · <b>Space</b> jump<br>' +
      '<b>Left</b> mine/attack · <b>Right</b> place<br><b>1-9</b>/scroll select · <b>E</b> inventory<br>' +
      '<b>To craft:</b> place a Crafting Table (slot 7), then <b>right-click</b> it';
    this.root.appendChild(this.hintEl);

    // Death overlay (interactive).
    this.deathEl = document.createElement('div');
    this.deathEl.style.cssText =
      'position:absolute;inset:0;display:none;flex-direction:column;align-items:center;justify-content:center;' +
      'background:rgba(60,0,0,.55);pointer-events:auto;';
    const title = document.createElement('div');
    title.textContent = 'You died';
    title.style.cssText =
      'font-size:44px;font-weight:700;margin-bottom:20px;text-shadow:0 2px 6px #000;';
    const btn = document.createElement('button');
    btn.textContent = 'Respawn';
    btn.style.cssText =
      'font-size:18px;padding:10px 28px;cursor:pointer;border:none;border-radius:6px;background:#6ab04c;color:#fff;';
    btn.addEventListener('click', () => this.onRespawn?.());
    this.deathEl.appendChild(title);
    this.deathEl.appendChild(btn);
    this.root.appendChild(this.deathEl);
  }

  setRespawnHandler(fn: () => void): void {
    this.onRespawn = fn;
  }

  update(state: {
    fps: number;
    inventory: Inventory;
    health: number;
    dead: boolean;
    kills: number;
    timeLabel: string;
    chunks: number;
    mobs: number;
    miningProgress: number | null;
    underwater: boolean;
    debug: string | null;
  }): void {
    this.fpsEl.textContent = `${state.fps} fps · ${state.chunks} chunks`;
    this.statusEl.innerHTML = `${state.timeLabel} · ${state.mobs} mobs<br>Zombies slain: ${state.kills}`;
    this.renderHotbar(state.inventory);
    this.renderHearts(state.health);
    this.deathEl.style.display = state.dead ? 'flex' : 'none';
    this.underwaterEl.style.display = state.underwater ? 'block' : 'none';

    if (state.debug !== null) {
      this.debugEl.style.display = 'block';
      this.debugEl.textContent = state.debug;
    } else {
      this.debugEl.style.display = 'none';
    }

    if (state.miningProgress !== null) {
      this.progressEl.style.display = 'block';
      (this.progressEl.firstElementChild as HTMLElement).style.width =
        `${Math.round(state.miningProgress * 100)}%`;
    } else {
      this.progressEl.style.display = 'none';
    }
  }

  /** Remove all HUD DOM (when leaving the world). */
  dispose(): void {
    this.root.remove();
  }

  private renderHotbar(inv: Inventory): void {
    // Only the first HOTBAR_SIZE slots are the always-visible hotbar.
    this.hotbarEl.innerHTML = '';
    for (let i = 0; i < HOTBAR_SIZE; i++) {
      const slot = inv.slots[i];
      const cell = document.createElement('div');
      const selected = i === inv.selected;
      cell.style.cssText =
        `width:44px;height:44px;box-sizing:border-box;border:2px solid ${selected ? '#fff' : 'rgba(255,255,255,.35)'};` +
        'background:rgba(0,0,0,.35);position:relative;display:flex;align-items:center;justify-content:center;';
      if (slot) {
        const sw = document.createElement('div');
        sw.style.cssText = `width:26px;height:26px;background:${itemSwatch(slot.item)};border:1px solid rgba(0,0,0,.4);`;
        sw.title = itemName(slot.item);
        cell.appendChild(sw);
        if (slot.count > 1) {
          const cnt = document.createElement('div');
          cnt.textContent = String(slot.count);
          cnt.style.cssText =
            'position:absolute;right:2px;bottom:0;font-size:12px;text-shadow:0 1px 2px #000;';
          cell.appendChild(cnt);
        }
      }
      this.hotbarEl.appendChild(cell);
    }
  }

  private renderHearts(health: number): void {
    this.heartsEl.innerHTML = '';
    const total = MAX_HEALTH / 2; // 10 hearts
    for (let i = 0; i < total; i++) {
      const hp = health - i * 2;
      const fill = hp >= 2 ? 'full' : hp === 1 ? 'half' : 'empty';
      const heart = document.createElement('div');
      const color = fill === 'empty' ? '#3a1414' : '#e23b3b';
      heart.style.cssText = `width:16px;height:16px;font-size:16px;line-height:16px;color:${color};text-shadow:0 1px 1px #000;`;
      heart.textContent = fill === 'half' ? '◗' : '♥';
      this.heartsEl.appendChild(heart);
    }
  }
}
